import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Ton } from './KpiKart';

/**
 * GRAFİKLER — bağımlılıksız SVG. Renkler CSS değişkenlerinden (`--ccd-ton-*`).
 * Her grafik `role="img"` + `aria-label` taşır ve altında ekran okuyucu için
 * gizli bir tablo basar; fareyle üzerine gelince tam değer ipucu çıkar.
 */
const renk = (ton: Ton) => `var(--ccd-ton-${ton})`;
export const DIZI_TONLARI: Ton[] = ['mavi', 'yesil', 'turuncu', 'mor', 'kirmizi', 'gri'];

/** Kapsayıcının genişliğini izler (SVG metinleri bozulmadan ölçeklensin diye). */
function useGenislik<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** Eksen için "güzel" adımlar: 0'ı içerir, en çok ~5 çizgi. */
function olcek(enAz: number, enCok: number) {
  const ust = Math.max(enCok, 0); const alt = Math.min(enAz, 0);
  const aralik = ust - alt || 1;
  const ham = aralik / 4;
  const us = 10 ** Math.floor(Math.log10(ham));
  const adim = [1, 2, 2.5, 5, 10].map((k) => k * us).find((a) => a >= ham) ?? ham;
  const a0 = Math.floor(alt / adim) * adim; const a1 = Math.ceil(ust / adim) * adim;
  const cizgiler: number[] = [];
  for (let v = a0; v <= a1 + adim / 2; v += adim) cizgiler.push(Math.round(v));
  return { alt: a0, ust: a1 || adim, cizgiler };
}

export type Seri = { ad: string; ton: Ton };
export type Nokta = { etiket: string; degerler: number[]; /** İpucu başlığı (etiketin uzun hâli). */ baslik?: string };
type GrafikProps = {
  veri: Nokta[]; seriler: Seri[];
  /** Eksen yazısı (kısa). */ eksen: (v: number) => string;
  /** İpucundaki tam değer. */ tam: (v: number) => string;
  ad: string; yukseklik?: number;
  /** Fihrist maddesinin kimliği: grafiğe sağ tıklayınca "Bu nedir?" açılır. */
  fihrist?: string;
};

function Lejant({ seriler }: { seriler: Seri[] }) {
  if (seriler.length < 2) return null;
  return (
    <div className="ccd-grafik-lejant">
      {seriler.map((s) => <span key={s.ad}><i style={{ background: renk(s.ton) }} />{s.ad}</span>)}
    </div>
  );
}

function GizliTablo({ veri, seriler, tam, ad }: Pick<GrafikProps, 'veri' | 'seriler' | 'tam' | 'ad'>) {
  return (
    <div className="ccd-gizli"><table>
      <caption>{ad}</caption>
      <thead><tr><th>Dönem</th>{seriler.map((s) => <th key={s.ad}>{s.ad}</th>)}</tr></thead>
      <tbody>{veri.map((n, i) => <tr key={i}><td>{n.baslik ?? n.etiket}</td>{n.degerler.map((d, j) => <td key={j}>{tam(d)}</td>)}</tr>)}</tbody>
    </table></div>
  );
}

function Balon({ nokta, seriler, tam, sol }: { nokta: Nokta; seriler: Seri[]; tam: (v: number) => string; sol: number }) {
  return (
    <div className="ccd-grafik-balon" style={{ left: `${sol}%`, transform: `translateX(${sol > 70 ? '-100%' : sol < 30 ? '0' : '-50%'})` }}>
      <b>{nokta.baslik ?? nokta.etiket}</b>
      {seriler.map((s, i) => <span key={s.ad}><i style={{ background: renk(s.ton) }} />{s.ad}: <b>{tam(nokta.degerler[i] ?? 0)}</b></span>)}
    </div>
  );
}

const KENAR = { sol: 58, sag: 8, ust: 10, alt: 26 };

/** Eksen çizgileri ve yazıları (çubuk ve çizgi ortak). */
function Eksenler({ w, h, o, eksen, y }: { w: number; h: number; o: ReturnType<typeof olcek>; eksen: (v: number) => string; y: (v: number) => number }) {
  return (
    <g>
      {o.cizgiler.map((v) => (
        <g key={v}>
          <line x1={KENAR.sol} x2={w - KENAR.sag} y1={y(v)} y2={y(v)} className={v === 0 ? 'ccd-grafik-sifir' : 'ccd-grafik-cizgi'} />
          <text x={KENAR.sol - 8} y={y(v)} dy="0.32em" textAnchor="end" className="ccd-grafik-yazi">{eksen(v)}</text>
        </g>
      ))}
      <line x1={KENAR.sol} x2={w - KENAR.sag} y1={h - KENAR.alt} y2={h - KENAR.alt} className="ccd-grafik-cizgi" opacity={0} />
    </g>
  );
}

/** Yatay eksende sığacak kadar etiket: her k'ıncısı yazılır. */
const etiketAdimi = (adet: number, w: number, enUzun: number) => Math.max(1, Math.ceil((adet * (enUzun * 6.4 + 10)) / Math.max(1, w - KENAR.sol - KENAR.sag)));

/** DİKEY ÇUBUK — tek ya da çift seri (ciro + alış). Eksi değer sıfır çizgisinin altına iner. */
export function CubukGrafik({ veri, seriler, eksen, tam, ad, yukseklik = 260, fihrist }: GrafikProps) {
  const [ref, w] = useGenislik<HTMLDivElement>();
  const [secili, setSecili] = useState<number | null>(null);
  const tum = veri.flatMap((n) => n.degerler);
  const o = olcek(Math.min(0, ...tum), Math.max(0, ...tum));
  const h = yukseklik;
  const ic = Math.max(1, w - KENAR.sol - KENAR.sag);
  const y = (v: number) => KENAR.ust + (1 - (v - o.alt) / (o.ust - o.alt)) * (h - KENAR.ust - KENAR.alt);
  const dilim = ic / Math.max(1, veri.length);
  const grup = Math.min(dilim * 0.72, seriler.length * 34);
  const cubuk = grup / seriler.length;
  const adim = etiketAdimi(veri.length, w, Math.max(...veri.map((n) => n.etiket.length), 1));
  const bos = tum.every((v) => v === 0);
  return (
    <div className="ccd-grafik" ref={ref} data-fihrist={fihrist}>
      <Lejant seriler={seriler} />
      <div className="ccd-grafik-alan" style={{ height: h }} onMouseLeave={() => setSecili(null)}>
        {w > 0 && (
          <svg width={w} height={h} role="img" aria-label={ad}>
            <Eksenler w={w} h={h} o={o} eksen={eksen} y={y} />
            {veri.map((n, i) => {
              const x0 = KENAR.sol + i * dilim;
              return (
                <g key={i}>
                  {secili === i && <rect x={x0} y={KENAR.ust} width={dilim} height={h - KENAR.ust - KENAR.alt} className="ccd-grafik-vurgu" />}
                  {n.degerler.map((d, j) => {
                    const ust = y(Math.max(d, 0)); const alt = y(Math.min(d, 0));
                    return <rect key={j} x={x0 + (dilim - grup) / 2 + j * cubuk + 1} y={ust} width={Math.max(1, cubuk - 2)} height={Math.max(d === 0 ? 0 : 1.5, alt - ust)}
                                 rx={Math.min(3, cubuk / 3)} fill={renk(seriler[j].ton)} opacity={secili == null || secili === i ? 1 : 0.55} />;
                  })}
                  {i % adim === 0 && <text x={x0 + dilim / 2} y={h - 8} textAnchor="middle" className="ccd-grafik-yazi">{n.etiket}</text>}
                  <rect x={x0} y={0} width={dilim} height={h} fill="transparent" onMouseEnter={() => setSecili(i)} onClick={() => setSecili(i)}>
                    <title>{`${n.baslik ?? n.etiket} — ${seriler.map((s, j) => `${s.ad}: ${tam(n.degerler[j] ?? 0)}`).join(' · ')}`}</title>
                  </rect>
                </g>
              );
            })}
          </svg>
        )}
        {bos && <div className="ccd-grafik-bos">Bu dönemde veri yok</div>}
        {secili != null && veri[secili] && <Balon nokta={veri[secili]} seriler={seriler} tam={tam} sol={((KENAR.sol + (secili + 0.5) * dilim) / Math.max(1, w)) * 100} />}
      </div>
      <GizliTablo veri={veri} seriler={seriler} tam={tam} ad={ad} />
    </div>
  );
}

/** ÇİZGİ / ALAN — tek serinin gidişi (trend). */
export function CizgiGrafik({ veri, seriler, eksen, tam, ad, yukseklik = 240, fihrist }: GrafikProps) {
  const [ref, w] = useGenislik<HTMLDivElement>();
  const [secili, setSecili] = useState<number | null>(null);
  const kimlik = useId().replace(/:/g, '');
  const tum = veri.flatMap((n) => n.degerler);
  const o = olcek(Math.min(0, ...tum), Math.max(0, ...tum));
  const h = yukseklik;
  const ic = Math.max(1, w - KENAR.sol - KENAR.sag);
  const y = (v: number) => KENAR.ust + (1 - (v - o.alt) / (o.ust - o.alt)) * (h - KENAR.ust - KENAR.alt);
  const dilim = ic / Math.max(1, veri.length);
  const x = (i: number) => KENAR.sol + (i + 0.5) * dilim;
  const adim = etiketAdimi(veri.length, w, Math.max(...veri.map((n) => n.etiket.length), 1));
  const bos = tum.every((v) => v === 0);
  return (
    <div className="ccd-grafik" ref={ref} data-fihrist={fihrist}>
      <Lejant seriler={seriler} />
      <div className="ccd-grafik-alan" style={{ height: h }} onMouseLeave={() => setSecili(null)}>
        {w > 0 && (
          <svg width={w} height={h} role="img" aria-label={ad}>
            <defs>
              {seriler.map((s, j) => (
                <linearGradient key={j} id={`${kimlik}-${j}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={renk(s.ton)} stopOpacity="0.22" /><stop offset="100%" stopColor={renk(s.ton)} stopOpacity="0" />
                </linearGradient>
              ))}
            </defs>
            <Eksenler w={w} h={h} o={o} eksen={eksen} y={y} />
            {seriler.map((s, j) => {
              const yol = veri.map((n, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(n.degerler[j] ?? 0).toFixed(1)}`).join(' ');
              return (
                <g key={j}>
                  {veri.length > 1 && j === 0 && <path d={`${yol} L${x(veri.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`} fill={`url(#${kimlik}-${j})`} />}
                  <path d={yol} fill="none" stroke={renk(s.ton)} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
                  {(veri.length <= 16 || secili != null) && veri.map((n, i) => (veri.length <= 16 || secili === i) && (
                    <circle key={i} cx={x(i)} cy={y(n.degerler[j] ?? 0)} r={secili === i ? 4.5 : 3} fill="var(--ccd-bg-raised)" stroke={renk(s.ton)} strokeWidth="2" />
                  ))}
                </g>
              );
            })}
            {veri.map((n, i) => (
              <g key={i}>
                {secili === i && <line x1={x(i)} x2={x(i)} y1={KENAR.ust} y2={h - KENAR.alt} className="ccd-grafik-sifir" />}
                {i % adim === 0 && <text x={x(i)} y={h - 8} textAnchor="middle" className="ccd-grafik-yazi">{n.etiket}</text>}
                <rect x={KENAR.sol + i * dilim} y={0} width={dilim} height={h} fill="transparent" onMouseEnter={() => setSecili(i)} onClick={() => setSecili(i)}>
                  <title>{`${n.baslik ?? n.etiket} — ${seriler.map((s, j) => `${s.ad}: ${tam(n.degerler[j] ?? 0)}`).join(' · ')}`}</title>
                </rect>
              </g>
            ))}
          </svg>
        )}
        {bos && <div className="ccd-grafik-bos">Bu dönemde veri yok</div>}
        {secili != null && veri[secili] && <Balon nokta={veri[secili]} seriler={seriler} tam={tam} sol={(x(secili) / Math.max(1, w)) * 100} />}
      </div>
      <GizliTablo veri={veri} seriler={seriler} tam={tam} ad={ad} />
    </div>
  );
}

export type Dilim = { ad: string; deger: number; ton?: Ton; alt?: string };
/**
 * HALKA — dağılım. En çok `enCok` dilim; kalanlar "Diğer"de toplanır. Eksi ve
 * sıfır değerler halkaya girmez (pay hesabı yalnız artı değerler üzerinden).
 */
export function HalkaGrafik({ dilimler, tam, yaz, ad, orta, enCok = 6, fihrist }: { fihrist?: string; dilimler: Dilim[]; tam: (v: number) => string; /** Listede kısa yazım; verilmezse tam değer. */ yaz?: (v: number) => string; ad: string; orta?: { deger: string; etiket: string }; enCok?: number }) {
  const arti = dilimler.filter((d) => d.deger > 0).sort((a, b) => b.deger - a.deger);
  const gosterilen = arti.length > enCok
    ? [...arti.slice(0, enCok - 1), { ad: `Diğer (${arti.length - enCok + 1})`, deger: arti.slice(enCok - 1).reduce((t, d) => t + d.deger, 0), ton: 'gri' as Ton }]
    : arti;
  const toplam = gosterilen.reduce((t, d) => t + d.deger, 0);
  const R = 52; const cevre = 2 * Math.PI * R;
  let gecen = 0;
  if (toplam <= 0) return <div className="ccd-grafik-bos ccd-grafik-bos-duz">Bu dönemde veri yok</div>;
  return (
    <div className="ccd-halka" data-fihrist={fihrist}>
      <svg viewBox="0 0 140 140" role="img" aria-label={ad} className="ccd-halka-svg">
        <circle cx="70" cy="70" r={R} fill="none" stroke="var(--ccd-bg-subtle)" strokeWidth="20" />
        {gosterilen.map((d, i) => {
          const uzun = (d.deger / toplam) * cevre;
          const el = (
            <circle key={i} cx="70" cy="70" r={R} fill="none" stroke={renk(d.ton ?? DIZI_TONLARI[i % DIZI_TONLARI.length])} strokeWidth="20"
                    strokeDasharray={`${Math.max(0, uzun - (gosterilen.length > 1 ? 1.5 : 0))} ${cevre}`} strokeDashoffset={-gecen} transform="rotate(-90 70 70)">
              <title>{`${d.ad}: ${tam(d.deger)} (%${Math.round((d.deger / toplam) * 1000) / 10})`}</title>
            </circle>
          );
          gecen += uzun;
          return el;
        })}
        {orta && (
          <>
            <text x="70" y="68" textAnchor="middle" className="ccd-halka-orta">{orta.deger}</text>
            <text x="70" y="84" textAnchor="middle" className="ccd-halka-orta-etiket">{orta.etiket}</text>
          </>
        )}
      </svg>
      <ul className="ccd-halka-liste">
        {gosterilen.map((d, i) => (
          <li key={i} title={tam(d.deger)}>
            <i style={{ background: renk(d.ton ?? DIZI_TONLARI[i % DIZI_TONLARI.length]) }} />
            <span className="ccd-halka-ad">{d.ad}</span>
            <span className="ccd-halka-pay">%{new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format((d.deger / toplam) * 100)}</span>
            <span className="ccd-halka-deger">{(yaz ?? tam)(d.deger)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export type SiraSatiri = { ad: string; deger: number; alt?: ReactNode; to?: string; ton?: Ton };
/** YATAY SIRALI ÇUBUK — "ilk 10" listeleri. Çubuk boyu en büyük değere göredir. */
export function SiraliCubuk({ satirlar, ton = 'mavi', yaz, tam, ad, bos = 'Bu dönemde veri yok', fihrist }: {
  fihrist?: string; satirlar: SiraSatiri[]; ton?: Ton; yaz: (v: number) => string; tam?: (v: number) => string; ad: string; bos?: string;
}) {
  const enBuyuk = Math.max(...satirlar.map((s) => Math.abs(s.deger)), 0);
  if (satirlar.length === 0 || enBuyuk === 0) return <div className="ccd-grafik-bos ccd-grafik-bos-duz">{bos}</div>;
  return (
    <ol className="ccd-sirali" aria-label={ad} data-fihrist={fihrist}>
      {satirlar.map((s, i) => (
        <li key={i}>
          <span className="ccd-sirali-sira">{i + 1}</span>
          <div className="ccd-sirali-govde">
            <div className="ccd-sirali-ust">
              {s.to ? <Link to={s.to} className="ccd-sirali-ad" title={s.ad}>{s.ad}</Link> : <span className="ccd-sirali-ad" title={s.ad}>{s.ad}</span>}
              <b title={tam?.(s.deger)}>{yaz(s.deger)}</b>
            </div>
            <div className="ccd-sirali-ray"><span style={{ width: `${Math.max(1.5, (Math.abs(s.deger) / enBuyuk) * 100)}%`, background: renk(s.ton ?? ton) }} /></div>
            {s.alt && <div className="ccd-meta">{s.alt}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Yaşlandırma gibi az sayıda kova: etiket + oranlı çubuk + tutar. */
export function OranCubuklari({ satirlar, yaz, tam, fihrist }: { fihrist?: string; satirlar: { ad: string; deger: number; ton: Ton; alt?: string }[]; yaz: (v: number) => string; tam?: (v: number) => string }) {
  const toplam = satirlar.reduce((t, s) => t + Math.max(0, s.deger), 0);
  return (
    <div className="ccd-oran" data-fihrist={fihrist}>
      {satirlar.map((s) => {
        const pct = toplam > 0 ? (Math.max(0, s.deger) / toplam) * 100 : 0;
        return (
          <div key={s.ad} className={`ccd-oran-satir ccd-ton-${s.ton}${s.deger === 0 ? ' ccd-kpi-soluk' : ''}`}>
            <div className="ccd-oran-ust"><span>{s.ad}</span><b title={tam?.(s.deger)}>{yaz(s.deger)}</b></div>
            <div className="ccd-sirali-ray"><span style={{ width: `${pct}%`, background: renk(s.ton) }} /></div>
            <div className="ccd-meta">%{new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(pct)}{s.alt ? ` · ${s.alt}` : ''}</div>
          </div>
        );
      })}
    </div>
  );
}
